import {
  agreeDraw,
  applyUci,
  capMoves,
  flag,
  newGame,
  resign as resignGame,
  type Game,
} from '../game/game.js'
import { botLabel } from '../bot/models.js'
import { isGameOver, winnerOf } from '../game/status.js'
import { BLACK, WHITE, opposite } from '../primitives/color.js'
import { assertNever, err, ok, type Result } from '../result.js'
import {
  applyMoveToClock,
  flagFallAt,
  hasFlagged,
  initialClock,
  remainingMs,
  startClock,
  stopClock,
} from './clock.js'
import type { Command, RoomError, RoomEvent } from './events.js'
import {
  currentGame,
  colorOfSlot,
  modelOfSlot,
  slotOfColor,
  MAX_CHAT_LENGTH,
  MAX_NAME_LENGTH,
  OPPONENT,
  REACTIONS,
  REACTION_HISTORY,
  type Actor,
  type GameSlot,
  type Opponent,
  type PlayerSlot,
  type RoomId,
  type RoomState,
  type Score,
  type TimeControl,
  type Timestamp,
} from './types.js'

/**
 * How long an ai-vs-ai match is allowed to run before it is adjudicated a
 * draw. Two weak bots can shuffle indefinitely without tripping the ordinary
 * fifty-move/repetition rules for a long time; this is a much tighter,
 * cost-motivated ceiling, so it only ever applies when neither seat is human.
 */
export const AI_MATCH_MOVE_CAP_PLIES = 160

const freshGame = (): Game => {
  const started = newGame()
  /* c8 ignore next */
  if (!started.ok) throw new Error('the standard starting position is unparseable — this is a bug')
  return started.value
}

export const DEFAULT_NAME = 'Anonymous'

/** Trim, flatten whitespace, drop control characters, clamp. */
export const sanitizeName = (raw: string): string => {
  // eslint-disable-next-line no-control-regex
  const cleaned = raw
    .replace(/[\u0000-\u001f\u007f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  return cleaned === '' ? DEFAULT_NAME : cleaned.slice(0, MAX_NAME_LENGTH)
}

/**
 * A seat with a model is "arrived" the moment the room exists — for the
 * ordinary human-vs-bot case, so the clock starts as soon as the human opens
 * their link rather than waiting for an arrival that is never coming.
 *
 * `ai-vs-ai` is the deliberate exception: seating *both* bots arrived here
 * would never fire `clockStartEvents` at all, since that only runs as a side
 * effect of processing an `arrive` *command* through `decide()` — never from
 * initial state. With no human to send one, the room object itself sends two
 * synthetic `arrive` commands right after creation (see `RoomObject`), and
 * the second one is what actually starts the clock — the same mechanism a
 * real arrival uses, just driven by the object instead of a socket.
 */
const seatFor = (opponent: Opponent, slot: PlayerSlot): RoomState['players'][number] => {
  const model = modelOfSlot(opponent, slot)
  return model !== null && opponent.kind !== 'ai-vs-ai'
    ? { name: botLabel(model), arrived: true, avatar: null }
    : { name: DEFAULT_NAME, arrived: false, avatar: null }
}

export const createRoom = (
  id: RoomId,
  timeControl: TimeControl,
  createdAt: Timestamp,
  opponent: Opponent = { kind: 'human' },
): RoomState => ({
  id,
  timeControl,
  opponent,
  players: [seatFor(opponent, 0), seatFor(opponent, 1)],
  games: [{ index: 0, game: freshGame(), clock: initialClock(timeControl), drawOfferedBy: null }],
  score: { wins: [0, 0], draws: 0 },
  chat: [],
  reactions: [],
  rematchOfferedBy: null,
  createdAt,
})

/**
 * Derived from scratch on every reduction rather than incremented, which makes
 * double-counting a finished game structurally impossible.
 */
const scoreOf = (games: readonly GameSlot[]): Score => {
  const wins: [number, number] = [0, 0]
  let draws = 0
  for (const slot of games) {
    if (!isGameOver(slot.game.status)) continue
    const winner = winnerOf(slot.game.status)
    if (winner === null) draws += 1
    else wins[slotOfColor(winner, slot.index)] += 1
  }
  return { wins, draws }
}

const replaceCurrent = (state: RoomState, slot: GameSlot): RoomState => ({
  ...state,
  games: [...state.games.slice(0, -1), slot],
})

/** End the live game and stop its clock in one step. */
const endCurrent = (state: RoomState, game: Game, at: Timestamp): RoomState => {
  const slot = currentGame(state)
  return replaceCurrent(state, {
    ...slot,
    game,
    clock: stopClock(slot.clock, slot.game.position.turn, at),
    drawOfferedBy: null,
  })
}

/**
 * Fold one event into the room.
 *
 * Total and deterministic. It trusts its input: only `decide` produces events,
 * and only `decide`'s output is ever persisted, so an event that cannot apply
 * leaves the state untouched rather than throwing.
 */
export const reduce = (state: RoomState, event: RoomEvent): RoomState => {
  const next = ((): RoomState => {
    switch (event.type) {
      case 'playerArrived': {
        const players: [RoomState['players'][0], RoomState['players'][1]] = [...state.players]
        players[event.slot] = { name: event.name, arrived: true, avatar: event.avatar ?? null }
        return { ...state, players }
      }

      case 'playerRenamed': {
        const players: [RoomState['players'][0], RoomState['players'][1]] = [...state.players]
        players[event.slot] = { ...players[event.slot], name: event.name }
        return { ...state, players }
      }

      case 'clockStarted': {
        const slot = currentGame(state)
        return replaceCurrent(state, { ...slot, clock: startClock(slot.clock, event.at) })
      }

      case 'moveMade': {
        const slot = currentGame(state)
        const mover = slot.game.position.turn
        const advanced = applyUci(slot.game, event.uci)
        if (!advanced.ok) return state

        const clock = applyMoveToClock(slot.clock, state.timeControl, mover, event.at)
        return replaceCurrent(state, {
          ...slot,
          game: advanced.value,
          // A move answers any outstanding draw offer with "no".
          drawOfferedBy: null,
          clock: isGameOver(advanced.value.status) ? stopClock(clock, opposite(mover), event.at) : clock,
        })
      }

      case 'moveCapped': {
        const slot = currentGame(state)
        const capped = capMoves(slot.game)
        return capped.ok ? endCurrent(state, capped.value, event.at) : state
      }

      case 'drawOffered':
        return replaceCurrent(state, { ...currentGame(state), drawOfferedBy: event.slot })

      case 'drawResponded': {
        const slot = currentGame(state)
        if (!event.accepted) return replaceCurrent(state, { ...slot, drawOfferedBy: null })
        const agreed = agreeDraw(slot.game)
        return agreed.ok ? endCurrent(state, agreed.value, event.at) : state
      }

      case 'resigned': {
        const slot = currentGame(state)
        const resigned = resignGame(slot.game, colorOfSlot(event.slot, slot.index))
        return resigned.ok ? endCurrent(state, resigned.value, event.at) : state
      }

      case 'flagged': {
        const slot = currentGame(state)
        const flagged = flag(slot.game, event.color)
        return flagged.ok ? endCurrent(state, flagged.value, event.at) : state
      }

      case 'rematchOffered':
        return { ...state, rematchOfferedBy: event.slot }

      case 'rematchStarted':
        return {
          ...state,
          rematchOfferedBy: null,
          games: [
            ...state.games,
            {
              index: state.games.length,
              game: freshGame(),
              clock: initialClock(state.timeControl),
              drawOfferedBy: null,
            },
          ],
        }

      case 'chatSent':
        return {
          ...state,
          chat: [...state.chat, { from: event.from, name: event.name, text: event.text, at: event.at }],
        }

      case 'reacted':
        return {
          ...state,
          reactions: [...state.reactions, { slot: event.slot, emoji: event.emoji, at: event.at }].slice(
            -REACTION_HISTORY,
          ),
        }

      default:
        return assertNever(event, 'room event')
    }
  })()

  return next.games === state.games ? next : { ...next, score: scoreOf(next.games) }
}

export const replay = (state: RoomState, events: readonly RoomEvent[]): RoomState =>
  events.reduce(reduce, state)

export interface DecideContext {
  readonly now: Timestamp
  readonly actor: Actor
}

/**
 * Should the clock start? Only once both seats have connected and the live game
 * has not begun — which is what stops White's clock burning down while they
 * wait for a friend to open the link.
 */
const clockStartEvents = (state: RoomState, at: Timestamp): readonly RoomEvent[] => {
  const slot = currentGame(state)
  const bothHere = state.players[0].arrived && state.players[1].arrived
  if (!bothHere || slot.clock.turnStartedAt !== null || isGameOver(slot.game.status)) return []
  return [{ type: 'clockStarted', at }]
}

/**
 * A flag that has already fallen outranks whatever the caller was trying to do,
 * including a move that arrived late. Stamped with the instant the clock
 * actually hit zero rather than "now", so the log stays truthful.
 */
const expiryEvents = (state: RoomState, now: Timestamp): readonly RoomEvent[] => {
  const slot = currentGame(state)
  if (isGameOver(slot.game.status)) return []
  const turn = slot.game.position.turn
  if (!hasFlagged(slot.clock, turn, now)) return []
  return [{ type: 'flagged', color: turn, at: flagFallAt(slot.clock, turn) ?? now }]
}

const playerSlot = (actor: Actor): PlayerSlot | null => (actor.kind === 'player' ? actor.slot : null)

/**
 * Validate a command against the room and return the events it produces.
 *
 * Pure: every input is an argument, including the clock. That is what lets
 * "White disconnects on low time and rejoins after the flag falls" be an
 * ordinary unit test instead of a two-browser dance.
 */
export const decide = (
  state: RoomState,
  command: Command,
  { now, actor }: DecideContext,
): Result<readonly RoomEvent[], RoomError> => {
  const expired = expiryEvents(state, now)
  if (expired.length > 0) return ok(expired)
  if (command.type === 'tick') return ok([])

  const slot = currentGame(state)
  const me = playerSlot(actor)

  switch (command.type) {
    case 'chat': {
      const text = command.text.replace(/\s+/g, ' ').trim()
      if (text === '') return err({ kind: 'emptyMessage' })
      if (text.length > MAX_CHAT_LENGTH) return err({ kind: 'messageTooLong', limit: MAX_CHAT_LENGTH })
      const from = me ?? 'spectator'
      const name = me === null ? 'Spectator' : state.players[me].name
      return ok([{ type: 'chatSent', from, name, text, at: now }])
    }

    case 'arrive': {
      if (me === null) return err({ kind: 'notAPlayer' })
      const name = sanitizeName(command.name)
      if (state.players[me].arrived) {
        return ok(state.players[me].name === name ? [] : [{ type: 'playerRenamed', slot: me, name, at: now }])
      }
      const arrived: RoomEvent = {
        type: 'playerArrived',
        slot: me,
        name,
        avatar: command.avatar ?? null,
        at: now,
      }
      return ok([arrived, ...clockStartEvents(reduce(state, arrived), now)])
    }

    case 'rename': {
      if (me === null) return err({ kind: 'notAPlayer' })
      const name = sanitizeName(command.name)
      return ok(state.players[me].name === name ? [] : [{ type: 'playerRenamed', slot: me, name, at: now }])
    }

    case 'move': {
      if (me === null) return err({ kind: 'notAPlayer' })
      if (isGameOver(slot.game.status)) return err({ kind: 'gameOver' })
      if (!state.players[0].arrived || !state.players[1].arrived) {
        return err({ kind: 'waitingForOpponent' })
      }
      if (colorOfSlot(me, slot.index) !== slot.game.position.turn) return err({ kind: 'notYourTurn' })
      const advanced = applyUci(slot.game, command.uci)
      if (!advanced.ok) return err({ kind: 'illegalMove', uci: command.uci })
      const capped =
        state.opponent.kind === 'ai-vs-ai' && advanced.value.history.length >= AI_MATCH_MOVE_CAP_PLIES
      return ok([
        { type: 'moveMade', uci: command.uci, at: now },
        ...(capped ? [{ type: 'moveCapped', at: now } as const] : []),
      ])
    }

    case 'resign': {
      if (me === null) return err({ kind: 'notAPlayer' })
      if (isGameOver(slot.game.status)) return err({ kind: 'gameOver' })
      return ok([{ type: 'resigned', slot: me, at: now }])
    }

    case 'offerDraw': {
      if (me === null) return err({ kind: 'notAPlayer' })
      if (isGameOver(slot.game.status)) return err({ kind: 'gameOver' })
      if (slot.drawOfferedBy === me) return err({ kind: 'drawAlreadyOffered' })
      // Offering into an open offer is simply accepting it.
      if (slot.drawOfferedBy === OPPONENT[me]) {
        return ok([{ type: 'drawResponded', slot: me, accepted: true, at: now }])
      }
      return ok([{ type: 'drawOffered', slot: me, at: now }])
    }

    case 'respondDraw': {
      if (me === null) return err({ kind: 'notAPlayer' })
      if (isGameOver(slot.game.status)) return err({ kind: 'gameOver' })
      if (slot.drawOfferedBy !== OPPONENT[me]) return err({ kind: 'noDrawOffer' })
      return ok([{ type: 'drawResponded', slot: me, accepted: command.accept, at: now }])
    }

    case 'offerRematch': {
      if (me === null) return err({ kind: 'notAPlayer' })
      if (!isGameOver(slot.game.status)) return err({ kind: 'gameStillGoing' })
      if (state.rematchOfferedBy !== OPPONENT[me]) {
        return ok([{ type: 'rematchOffered', slot: me, at: now }])
      }
      const started: RoomEvent = { type: 'rematchStarted', at: now }
      return ok([started, ...clockStartEvents(reduce(state, started), now)])
    }

    case 'react': {
      if (me === null) return err({ kind: 'notAPlayer' })
      if (!(REACTIONS as readonly string[]).includes(command.emoji)) {
        return err({ kind: 'unknownReaction', emoji: command.emoji })
      }
      return ok([{ type: 'reacted', slot: me, emoji: command.emoji, at: now }])
    }

    default:
      return assertNever(command, 'room command')
  }
}

/**
 * When the Durable Object should wake up to end the game on time, or null if no
 * clock is running.
 */
export const nextAlarmAt = (state: RoomState): Timestamp | null => {
  const slot = currentGame(state)
  if (isGameOver(slot.game.status)) return null
  return flagFallAt(slot.clock, slot.game.position.turn)
}

/** Remaining time for both sides at `now`, for rendering. */
export const clockView = (
  state: RoomState,
  now: Timestamp,
): { readonly whiteMs: number; readonly blackMs: number } => {
  const { clock, game } = currentGame(state)
  const turn = game.position.turn
  return {
    whiteMs: remainingMs(clock, turn, WHITE, now),
    blackMs: remainingMs(clock, turn, BLACK, now),
  }
}
